import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ChartType, Timeframe, DrawingTool, IndicatorConfig, Drawing } from '../types'
import { v4 as uuidv4 } from 'uuid'

interface ChartStore {
  // Symbol & Price
  symbol: string
  setSymbol: (symbol: string) => void
  currentPrice: number
  priceChange24h: number
  setPriceData: (price: number, change: number) => void
  
  // Chart Settings
  chartType: ChartType
  setChartType: (type: ChartType) => void
  timeframe: Timeframe
  setTimeframe: (tf: Timeframe) => void
  priceScale: 'normal' | 'log' | 'percentage'
  setPriceScale: (scale: 'normal' | 'log' | 'percentage') => void
  autoScale: boolean
  setAutoScale: (auto: boolean) => void
  
  // Indicators
  indicators: IndicatorConfig[]
  addIndicator: (type: IndicatorConfig['type'], params?: Record<string, any>) => void
  removeIndicator: (id: string) => void
  updateIndicator: (id: string, updates: Partial<IndicatorConfig>) => void
  
  // Drawings
  drawings: Drawing[]
  addDrawing: (drawing: Omit<Drawing, 'id'>) => void
  removeDrawing: (id: string) => void
  updateDrawing: (id: string, updates: Partial<Drawing>) => void
  clearDrawings: () => void
  
  // Drawing Tool
  activeDrawingTool: DrawingTool
  setActiveDrawingTool: (tool: DrawingTool) => void
  
  // WebSocket Status
  wsConnected: boolean
  setWsConnected: (connected: boolean) => void
}

const defaultIndicatorParams: Record<string, Record<string, any>> = {
  SMA: { period: 20, source: 'close' },
  EMA: { period: 20, source: 'close' },
  WMA: { period: 20, source: 'close' },
  RSI: { period: 14, source: 'close' },
  MACD: { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 },
  STOCH: { kPeriod: 14, dPeriod: 3, smooth: 3 },
  BB: { period: 20, stdDev: 2, source: 'close' },
  ATR: { period: 14 },
  ADX: { period: 14 },
  ICHIMOKU: { tenkanPeriod: 9, kijunPeriod: 26, senkouSpanBPeriod: 52 },
  VOL: {},
  VWAP: {},
  OBV: {},
  PSAR: { start: 0.02, increment: 0.02, max: 0.2 },
  SUPERTREND: { period: 10, multiplier: 3 },
  CCI: { period: 20 },
  MFI: { period: 14 },
  KC: { period: 20, multiplier: 1.5 },
  WILLR: { period: 14 },
  AROON: { period: 25 },
  DMI: { period: 14 },
  ROC: { period: 9 },
}

const indicatorColors: Record<string, string> = {
  SMA: '#ffaa00',
  EMA: '#00d4ff',
  WMA: '#ff6b6b',
  RSI: '#9945ff',
  MACD: '#00ff88',
  BB: '#00d4ff',
  ATR: '#ff6b6b',
  ADX: '#00ff88',
}

const overlayIndicators = ['SMA', 'EMA', 'WMA', 'DEMA', 'TEMA', 'VWMA', 'HMA', 'BB', 'KC', 'DC', 'ICHIMOKU', 'PSAR', 'SUPERTREND', 'VWAP', 'PIVOTS']

export const useChartStore = create<ChartStore>()(
  persist(
    (set, get) => ({
      // Symbol & Price
      symbol: 'BTC/USDT',
      setSymbol: (symbol) => set({ symbol }),
      currentPrice: 0,
      priceChange24h: 0,
      setPriceData: (price, change) => set({ currentPrice: price, priceChange24h: change }),
      
      // Chart Settings
      chartType: 'candlestick',
      setChartType: (chartType) => set({ chartType }),
      timeframe: '1h',
      setTimeframe: (timeframe) => set({ timeframe }),
      priceScale: 'normal',
      setPriceScale: (priceScale) => set({ priceScale }),
      autoScale: true,
      setAutoScale: (autoScale) => set({ autoScale }),
      
      // Indicators
      indicators: [],
      addIndicator: (type, params = {}) => {
        const defaultParams = defaultIndicatorParams[type] || {}
        const newIndicator: IndicatorConfig = {
          id: uuidv4(),
          type,
          params: { ...defaultParams, ...params },
          color: indicatorColors[type] || '#00d4ff',
          visible: true,
          overlay: overlayIndicators.includes(type),
        }
        set({ indicators: [...get().indicators, newIndicator] })
      },
      removeIndicator: (id) => {
        set({ indicators: get().indicators.filter(i => i.id !== id) })
      },
      updateIndicator: (id, updates) => {
        set({
          indicators: get().indicators.map(i => 
            i.id === id ? { ...i, ...updates } : i
          )
        })
      },
      
      // Drawings
      drawings: [],
      addDrawing: (drawing) => {
        const newDrawing: Drawing = {
          ...drawing,
          id: uuidv4(),
        }
        set({ drawings: [...get().drawings, newDrawing] })
      },
      removeDrawing: (id) => {
        set({ drawings: get().drawings.filter(d => d.id !== id) })
      },
      updateDrawing: (id, updates) => {
        set({
          drawings: get().drawings.map(d => 
            d.id === id ? { ...d, ...updates } : d
          )
        })
      },
      clearDrawings: () => set({ drawings: [] }),
      
      // Drawing Tool
      activeDrawingTool: 'cursor',
      setActiveDrawingTool: (activeDrawingTool) => set({ activeDrawingTool }),
      
      // WebSocket
      wsConnected: false,
      setWsConnected: (wsConnected) => set({ wsConnected }),
    }),
    {
      name: 'kit-chart-settings',
      partialize: (state) => ({
        symbol: state.symbol,
        chartType: state.chartType,
        timeframe: state.timeframe,
        indicators: state.indicators,
        drawings: state.drawings,
        priceScale: state.priceScale,
        autoScale: state.autoScale,
      }),
    }
  )
)
